import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import { useToast } from "../../../context/ToastContext";
import { cloneAgent } from "../../../services/agentService";
import { useAgentDetails } from "../EditAgent/hooks/useAgentDetails";
import { debug } from "../../../utils/debugLogger";

/**
 * Clone page — duplicates an existing agent. The source's knowledge files, tools,
 * model and conversation starters are copied by the backend; here the user only
 * tunes the name and instructions before creating the clone. The form is
 * pre-filled from the source via the same useAgentDetails hook EditAgentForm uses.
 */
const CloneAgentForm: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId, selectedCustomerName } = useCustomerWorkspace();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sourceAgentId = searchParams.get("cloneFromAgentId") ?? "";
  const urlCustomerId = searchParams.get("customerId") ?? "";
  const effectiveCustomerId = urlCustomerId || selectedCustomerId || "";

  const selectedCustomer = useMemo(
    () => (effectiveCustomerId ? { customer_id: effectiveCustomerId } : null),
    [effectiveCustomerId]
  );

  const { loading: loadingSource, parsedDetails, error: sourceError } = useAgentDetails({
    selectedAgentId: sourceAgentId,
    selectedCustomer,
    idToken,
  });

  const [agentName, setAgentName] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  // Pre-fill once the source agent's details arrive.
  useEffect(() => {
    if (parsedDetails) {
      setAgentName(`Copy of ${parsedDetails.agentName}`);
      setInstructions(parsedDetails.instructions);
    }
  }, [parsedDetails]);

  const goBack = () =>
    navigate(effectiveCustomerId ? `/?customerId=${effectiveCustomerId}` : "/");

  const handleSubmit = async () => {
    const trimmedName = agentName.trim();
    if (!sourceAgentId || !effectiveCustomerId || !trimmedName || !instructions.trim()) {
      setMessage("Please provide a name and instructions.");
      return;
    }

    setSubmitting(true);
    setMessage("Cloning agent...");
    try {
      const { data } = await cloneAgent(
        {
          clone_from_aiagent_id: sourceAgentId,
          customer_id: effectiveCustomerId,
          agent_name: trimmedName,
          instructions,
        },
        idToken
      );
      showToast(`"${data.agent_name ?? trimmedName}" created from clone`, "success");
      navigate(`/?customerId=${effectiveCustomerId}`);
    } catch (error: any) {
      // The clone copies S3 files server-side; a gateway timeout means it is still
      // finishing in the background, so treat it as an optimistic success.
      if (error?.isGatewayTimeout || error?.message?.includes("GATEWAY_TIMEOUT")) {
        showToast("Clone is being created — it will appear shortly.", "info");
        navigate(`/?customerId=${effectiveCustomerId}`);
        return;
      }
      debug.error("Error cloning agent:", error);
      setMessage(error?.message || "Failed to clone agent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const ready = Boolean(sourceAgentId && parsedDetails);
  const canSubmit =
    ready && !submitting && agentName.trim().length > 0 && instructions.trim().length > 0;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Back */}
      <div className="mb-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-[#2f4941] transition-colors hover:bg-zinc-100"
        >
          <span className="material-icons-outlined text-lg">arrow_back</span>
          Back to agents
        </button>
      </div>

      <h1 className="text-xl font-bold mb-2">Clone Agent</h1>
      <p className="mb-6 text-sm text-theme-fg-muted">
        Creates a new agent that inherits this one's knowledge files, tools, model and conversation
        starters. Adjust the name and instructions below — everything else is copied automatically.
      </p>

      {/* Business Profile (read-only, from sidebar) */}
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-theme-border bg-theme-surface px-4 py-3">
        <span className="material-icons-outlined text-[20px] text-brand-primary">business</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-theme-fg-muted uppercase tracking-wide">Business Profile</p>
          {effectiveCustomerId ? (
            <p className="text-sm font-semibold text-theme-fg truncate">
              {selectedCustomerName ?? effectiveCustomerId}
            </p>
          ) : (
            <p className="text-sm text-amber-600 font-medium">
              No profile selected — use the sidebar to pick one
            </p>
          )}
        </div>
      </div>

      {/* Loading source */}
      {loadingSource && (
        <div className="mb-4 flex items-center gap-2 text-sm text-theme-fg-muted">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading agent to clone...
        </div>
      )}

      {/* Missing source guard */}
      {!sourceAgentId && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200">
          No source agent selected. Open this page from an agent's “Clone agent” menu.
        </div>
      )}

      {/* Source present but no business profile — guide the user to recover */}
      {sourceAgentId && !effectiveCustomerId && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200">
          Select a business profile in the sidebar to load this agent and clone it.
        </div>
      )}

      {/* Source load error */}
      {sourceError && (
        <div className="mb-4 px-4 py-2 rounded-lg text-sm bg-red-100 text-red-800 border border-red-300">
          {sourceError}
        </div>
      )}

      {/* Form — shown once the source agent is loaded */}
      {ready && (
        <>
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Agent Name</label>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              disabled={submitting}
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">Instructions</label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={10}
              className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              disabled={submitting}
            />
          </div>
        </>
      )}

      {/* Status message */}
      {message && (
        <div
          className={`mb-4 px-4 py-2 rounded-lg text-sm ${
            message.includes("Cloning")
              ? "bg-blue-100 text-blue-800 border border-blue-300"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {message}
        </div>
      )}

      {/* Submit */}
      {ready && (
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full py-2 px-4 rounded-lg text-white ${
            canSubmit ? "bg-brand-primary hover:bg-brand-primary-hover" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Cloning..." : "Create Clone"}
        </button>
      )}
    </div>
  );
};

export default CloneAgentForm;
